import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { useNavigate } from 'react-router-dom';
import MDButton from 'components/MDButton';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { openSnackbar } from '../../redux/slices/common';
import colors from 'assets/theme/base/colors';
import { deletePromo, getPromotionList } from '../../redux/slices/metrc';

export default function MemberMetrc() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageRef = useRef(0);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const gridRefApi = useRef(null);
    const { text } = colors;

    const handleSearch = useDebounce(() => {
        // Perform search operation with the debounced term
        pageRef.current = 0;
        searchFn();
    }, 500);

    const { loadingList: loading = false, promotionList = {} } = useSelector(state => state.metrc);

    const deleteHandler = (id) => {
        dispatch(setModalLoader(true));
        dispatch(deletePromo({ data: id, type: "member" })).unwrap().then((res) => {
            showError(res?.message, 'success');
            dispatch(closeCustomModalDialog());
            pageRef.current = 0;
            getRows(gridRefApi.current);
        }).catch((err) => {
            dispatch(setModalLoader(false));
            showError(err?.message, 'error');
        });
    };

    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };


    const columnDefs = [

        {
            headerName: 'Member Discount',
            field: 'memberDiscountValue',
            suppressMenu: false,
            sortable: false,
            minWidth: 160,
            cellRenderer: (props) => {
                return props?.value ? `${props?.value}%` : "0%"
            },
        },
        {
            headerName: 'Member  Percentage',
            field: 'memberDiscountPercentage',
            suppressMenu: false,
            sortable: false,
            minWidth: 160,
            cellRenderer: (props) => {
                return props?.value ? `${props?.value}%` : "0%"
            },
        },
        {
            headerName: 'Member Unique Id',
            field: 'memberTaxUniqueId',
            suppressMenu: false,
            minWidth: 160,
            sortable: false,
            cellRenderer: (props) => {
                return props?.value ? props?.value : "-"
            },
        },

        {
            headerName: 'Actions',
            sortable: false,
            cellRenderer: (props) => {
                return <Box display="inline-flex" alignItems="center">
                    <IconButton onClick={(e) => {
                        e.stopPropagation();
                        onEditClicked(props);
                    }} color='primary' size='small' variant="primary">
                        <EditIcon width='16px' height='16px' />
                    </IconButton>
                    <IconButton onClick={(e) => {
                        e.stopPropagation();
                        dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props.data?.memberTaxUniqueId }, modal: 'DELETE_MODAL' }));
                    }} color='error' size='small' variant="primary" sx={{ marginLeft: 1 }}>
                        <DeleteIcon width='16px' height='16px' />
                    </IconButton>
                </Box>;
            },
            cellStyle: { textAlign: 'right', justifyContent: 'end' },
            headerClass: 'right-aligned-cell-header',
            minWidth: 100,
            maxWidth: 100,
            pinned: 'right'
        },

    ];

    const getRows = async (params) => {
        gridRefApi.current = params;
        const sortModel = params?.sortModel;
        let sortedColumnName = '';
        let sortDirection = '';
        if (sortModel.length > 0) {
            const sortedColumn = sortModel[0];
            sortedColumnName = sortedColumn.colId;
            sortDirection = sortedColumn.sort === 'asc' ? true : false;
        }
        dispatch(getPromotionList({ limit: 20, skip: pageRef.current, searchTerm: searchTermRef.current, userType: 'admin', [sortedColumnName]: sortDirection, type: "member" })).unwrap().then(res => {
            const { data, total } = res;

            const modifiedBlogs = data?.map((item) => ({
                ...item,
            }));
            params.successCallback(modifiedBlogs, total);
            pageRef.current = pageRef.current + 20;
        }).catch(() => {
            params.successCallback([], 0);
        });
    };

    const searchFn = () => {
        const dataSource = {
            getRows
        };
        mainGridApiRef.current.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption('datasource', dataSource);
    };

    const onRowClicked = (row) => {
        if (row?.colDef?.headerName !== 'Action') {
        }
    };

    const onEditClicked = (row) => {

        dispatch(openCustomModalDialog({
            modal: 'ADD_METRC_DATA',
            modalData: {
                type: "Member",
                title: 'Edit Member',
                data: row?.data,
                edit: true,
                metrcHandler: metrcHandler
            }
        }))
    };


    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (promotionList?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [promotionList, loading]);

    const metrcHandler = () => {
        searchFn()

    }
    return (
        <>
            <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                    <Typography variant='h4'>Member</Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                        <MDButton
                            startIcon={<AddRoundedIcon fontSize='large' />}
                            variant="contained" color="primary"
                            onClick={() => {
                                dispatch(openCustomModalDialog({
                                    modal: 'ADD_METRC_DATA',
                                    modalData: {
                                        type: "Member",
                                        title: 'Add Member',
                                        data: {},
                                        edit: false,
                                        metrcHandler: metrcHandler
                                    }
                                }))
                            }} >
                            Add Member Discount
                        </MDButton>
                    </Stack>
                </Grid>
            </Grid>

            <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef}
                isLoading={loading} onRowClicked={onRowClicked} style={{ height: '71vh' }} />
        </>
    );
}